import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/core';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import styles from './facility-lms-cutover.style';
import superAdminTheme from '../../shared/components/data-table/superadmin-theme';
import DataTable from '../../shared/components/data-table/DataTable';
import StateBlock from '../../shared/components/data-table/StateBlock';
import { formatLastRefreshedTime } from '../lms-course-review/utils/lms-course-review.utils';
import { fetchFacilities, fetchFacilityHistory, switchFacilityLms } from './services/facility-lms.service';
import {
  filterFacilityRows,
  LMS_FILTER_ALL,
  normalizeDeliveryLms,
  summarizeFacilities,
  sumLearnerCounts,
} from './utils/facility-lms.utils';
import ProvisioningJobsPanel from './ProvisioningJobsPanel';
import FacilityLmsBulkBar from './FacilityLmsBulkBar';
import FacilityBulkJobsPanel from './FacilityBulkJobsPanel';
import useFacilityBulkJobsHook from './useFacilityBulkJobsHook';
import useFacilityBulkSelection from './useFacilityBulkSelection';
import { QueueSelectCell } from '../lms-course-review/components/queue-row-cells/queue-select-cell';
import { bulkProvisionLocations } from './services/lms-provisioning.service';
import { canBulkProvisionFacility, canProvisionFacility } from './utils/provisioning-jobs.utils';

const LMS_DISPLAY_NAMES = { d2l: 'D2L', canvas: 'Canvas' };

// Shared control height keeps the search field and segmented filter on the same baseline.
const CONTROL_HEIGHT = 40;

const LMS_FILTER_OPTIONS = [
  { value: LMS_FILTER_ALL, label: 'All', countKey: 'all' },
  { value: 'canvas', label: 'Canvas', countKey: 'canvas' },
  { value: 'd2l', label: 'D2L', countKey: 'd2l' },
];

const formatHistoryTimestamp = (iso) => {
  if (!iso) return 'Unknown date';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const facilityCountLabel = (count) => `${count} facilit${count === 1 ? 'y' : 'ies'}`;

const formatGridSubtitle = (loading, lastRefreshed, filteredCount, totalCount) => {
  if (loading) return 'Loading…';
  const scope = filteredCount === totalCount ? facilityCountLabel(filteredCount) : `${filteredCount} of ${totalCount}`;
  return `${formatLastRefreshedTime(lastRefreshed)} · ${scope}`;
};

const getLmsBadgeVariantClass = (classes, { isCanvas, isDefault }) => {
  if (isCanvas) return classes.lmsBadgeCanvas;
  if (isDefault) return classes.lmsBadgeD2lDefault;
  return classes.lmsBadgeD2l;
};

const getLmsBadgeClassName = (classes, { isCanvas, isDefault }) =>
  [classes.lmsBadge, getLmsBadgeVariantClass(classes, { isCanvas, isDefault })].filter(Boolean).join(' ');

const CurrentLmsChip = ({ value, defaultApplied, classes }) => {
  const lms = normalizeDeliveryLms(value);
  const isCanvas = lms === 'canvas';
  const isDefault = defaultApplied && !isCanvas;

  const badge = (
    <Box
      className={getLmsBadgeClassName(classes, { isCanvas, isDefault })}
      data-testid={`facility-lms-chip-${lms}${isDefault ? '-default' : ''}`}
    >
      {isCanvas ? <SchoolRoundedIcon /> : <MenuBookRoundedIcon />}
      <Box component="span">{LMS_DISPLAY_NAMES[lms]}</Box>
      {isDefault && (
        <Box component="span" className={classes.lmsBadgeDefaultTag}>
          Default
        </Box>
      )}
    </Box>
  );

  if (!isDefault) return badge;

  return (
    <Tooltip title="Not yet configured — learners default to D2L until a delivery LMS is set.">
      <Box component="span" className={classes.lmsBadgeTooltipWrap}>
        {badge}
      </Box>
    </Tooltip>
  );
};

const STAT_ITEMS = [
  { key: 'total', label: 'Facilities', description: 'Total in scope', countKey: 'total' },
  { key: 'canvas', label: 'On Canvas', description: 'Migrated', countKey: 'canvas', accent: 'primary.main' },
  { key: 'd2l', label: 'On D2L', description: 'Awaiting migration', countKey: 'd2l' },
  {
    key: 'percent',
    label: 'Migrated',
    description: 'Of all facilities',
    countKey: 'percentMigrated',
    accent: 'success.main',
    format: (stats) => `${stats.percentMigrated}%`,
  },
];

const StatCell = ({ label, value, description, accent, showDivider }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      gap: 0.5,
      px: { xs: 1.5, sm: 2.5 },
      py: 2,
      minHeight: 96,
      borderRight: showDivider ? { xs: 'none', sm: '1px solid' } : 'none',
      borderColor: 'divider',
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, lineHeight: 1.4 }}
    >
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, color: accent || 'text.primary' }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, minHeight: 18 }}>
      {description || '\u00A0'}
    </Typography>
  </Box>
);

const MigrationStatStrip = ({ stats }) => (
  <Paper variant="outlined" sx={{ borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 2, md: 2.5 }, pb: 0 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1.5 }}>
        Migration overview
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        alignItems: 'stretch',
      }}
    >
      {STAT_ITEMS.map((item, index) => (
        <StatCell
          key={item.key}
          label={item.label}
          value={item.format ? item.format(stats) : stats[item.countKey]}
          description={item.description}
          accent={item.accent}
          showDivider={index < STAT_ITEMS.length - 1}
        />
      ))}
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: { xs: 2, md: 2.5 },
        py: 2,
        mt: { xs: 0.5, sm: 0 },
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <LinearProgress
        variant="determinate"
        value={stats.percentMigrated}
        sx={{
          flexGrow: 1,
          height: 8,
          borderRadius: 4,
          backgroundColor: 'divider',
          '& .MuiLinearProgress-bar': { borderRadius: 4 },
        }}
        data-testid="facility-lms-progress"
      />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, textAlign: 'right', flexShrink: 0 }}>
        {stats.canvas} of {stats.total} on Canvas
      </Typography>
    </Box>
  </Paper>
);

const HistoryDialogBody = ({ historyModal }) => {
  if (!historyModal) return null;
  if (historyModal.loading) {
    return (
      <Box py={3}>
        <LinearProgress />
      </Box>
    );
  }
  if (historyModal.error) {
    return <Typography color="error">Failed to load history. Please try again.</Typography>;
  }
  if (historyModal.entries?.length === 0) {
    return <Typography color="text.secondary">No LMS switch history for this facility.</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {historyModal.entries.map((entry) => (
        <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, borderColor: 'divider', backgroundColor: 'grey.50' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {formatHistoryTimestamp(entry.createdAt)}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <Chip size="small" label={(entry.fromDeliveryLms || 'unknown').toUpperCase()} variant="outlined" />
            <SwapHorizRoundedIcon fontSize="small" color="action" />
            <Chip size="small" label={(entry.deliveryLms || 'unknown').toUpperCase()} color="primary" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            by {entry.createdBy || 'unknown'}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

const FacilityLmsCutover = ({ classes }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [lmsFilter, setLmsFilter] = useState(LMS_FILTER_ALL);
  const [confirm, setConfirm] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [provisioningModal, setProvisioningModal] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkJobsPanelOpen, setBulkJobsPanelOpen] = useState(false);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    setError(false);
    try {
      const data = await fetchFacilities();
      setRows(data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('[FacilityLmsCutover] Failed to load facilities', err);
      setError(true);
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(
    () => filterFacilityRows(rows, { search, lmsFilter }),
    [rows, search, lmsFilter]
  );

  const stats = useMemo(() => summarizeFacilities(rows), [rows]);
  const filterCounts = useMemo(
    () => ({ all: rows.length, canvas: stats.canvas, d2l: stats.d2l }),
    [rows.length, stats]
  );

  useEffect(() => {
    setPage(1);
  }, [search, lmsFilter]);

  const bulkSelection = useFacilityBulkSelection(filteredRows);
  const {
    selectedIds,
    selectedCount,
    selectedFacilityIds,
    toggleRow,
    clearSelection,
    toggleSelectAllOnPage,
    getPageSelectionState,
  } = bulkSelection;

  const currentPageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const pageSelectionState = useMemo(
    () => getPageSelectionState(currentPageRows),
    [getPageSelectionState, currentPageRows]
  );

  const bulkJobs = useFacilityBulkJobsHook({ enabled: bulkJobsPanelOpen });
  const refreshBulkJobs = bulkJobs.refresh;

  const handleBulkProvision = useCallback(async () => {
    if (selectedFacilityIds.length === 0 || bulkSubmitting) return;
    setBulkSubmitting(true);
    try {
      const result = await bulkProvisionLocations({
        scope: 'FACILITIES',
        facilityIds: selectedFacilityIds,
      });
      const jobId = result?.jobId ?? result?.id;
      const totalRecords = result?.totalRecords ?? selectedFacilityIds.length;
      setToast({
        message: `Bulk facility provisioning started (job #${jobId}, ${totalRecords} locations)`,
        type: 'success',
      });
      setBulkJobsPanelOpen(true);
      clearSelection();
      await Promise.all([load({ background: true }), refreshBulkJobs({ background: true })]);
    } catch (err) {
      console.error('[FacilityLmsCutover] Bulk facility provisioning failed', err);
      setToast({ message: 'Bulk provisioning failed. Please retry.', type: 'error' });
    } finally {
      setBulkSubmitting(false);
    }
  }, [selectedFacilityIds, bulkSubmitting, clearSelection, load, refreshBulkJobs]);

  const openHistory = useCallback(async (row) => {
    setHistoryModal({ row, entries: [], loading: true, error: false });
    try {
      const data = await fetchFacilityHistory(row.locationKey);
      setHistoryModal({ row, entries: data, loading: false, error: false });
    } catch (err) {
      console.error('[FacilityLmsCutover] Failed to load facility history', err);
      setHistoryModal((prev) => (prev ? { ...prev, loading: false, error: true } : null));
    }
  }, []);

  const closeHistoryModal = () => setHistoryModal(null);

  const openProvisioning = useCallback((row) => {
    setProvisioningModal({ row });
  }, []);

  const closeProvisioningModal = useCallback(() => {
    setProvisioningModal(null);
    // Pick up any provisioning-driven state changes (e.g. delivery LMS flipping
    // to Canvas after a manual switch outside the dialog) on close.
    load({ background: true });
  }, [load]);

  const refreshFacilities = useCallback(() => load(), [load]);

  const requestSwitch = useCallback((row) => {
    const currentLms = normalizeDeliveryLms(row.currentDeliveryLms);
    const nextLms = currentLms === 'canvas' ? 'd2l' : 'canvas';
    setConfirm({ row: { ...row, currentDeliveryLms: currentLms }, nextLms });
  }, []);

  // Triggered from the provisioning dialog's checklist when every
  // precondition (parent provisioned, facility provisioned, learners
  // provisioned) is satisfied. Closes the provisioning dialog and opens the
  // existing confirm dialog so we keep a single switch code path.
  const requestSwitchFromDialog = useCallback(
    (row) => {
      setProvisioningModal(null);
      requestSwitch(row);
    },
    [requestSwitch],
  );

  const onConfirmSwitch = async () => {
    if (!confirm) return;
    const { row, nextLms } = confirm;
    setSwitching(true);
    try {
      await switchFacilityLms(row.locationKey, nextLms);
      setToast({
        message: `Switched ${row.locationName || row.shortCode} to ${nextLms.toUpperCase()}`,
        type: 'success',
      });
      setConfirm(null);
      await load();
    } catch (err) {
      console.error('[FacilityLmsCutover] Failed to switch facility LMS', err);
      setToast({ message: 'Switch failed. Please retry.', type: 'error' });
    } finally {
      setSwitching(false);
    }
  };

  const subtitle = formatGridSubtitle(loading && rows.length === 0, lastRefreshed, filteredRows.length, rows.length);

  const columns = useMemo(
    () => [
      {
        name: 'select',
        displayName: '',
        width: 48,
        sortable: false,
        renderHeader: () => (
          <Checkbox
            checked={pageSelectionState.headerChecked}
            indeterminate={pageSelectionState.headerIndeterminate}
            disabled={pageSelectionState.headerDisabled}
            onChange={() => toggleSelectAllOnPage(currentPageRows)}
            color="primary"
            inputProps={{
              'aria-label': 'Select all eligible facilities on this page',
              'data-testid': 'facility-lms-select-all-page',
            }}
          />
        ),
        render: (rowData) => {
          const eligible = canBulkProvisionFacility(rowData);
          const provisionGate = canProvisionFacility(rowData, null);
          const checkbox = (
            <QueueSelectCell
              checked={selectedIds.has(String(rowData.locationKey))}
              disabled={!eligible}
              onToggle={() => toggleRow(rowData.locationKey)}
            />
          );
          if (eligible) return checkbox;
          return (
            <Tooltip title={provisionGate.reason || 'Cannot bulk provision this facility.'}>
              <span>{checkbox}</span>
            </Tooltip>
          );
        },
      },
      { name: 'locationName', displayName: 'Facility', sortable: true },
      { name: 'shortCode', displayName: 'Short code', sortable: true },
      {
        name: 'currentDeliveryLms',
        displayName: 'Current LMS',
        render: (rowData) => (
          <CurrentLmsChip
            value={rowData.currentDeliveryLms}
            defaultApplied={rowData.defaultApplied}
            classes={classes}
          />
        ),
      },
      {
        name: 'actions',
        displayName: 'Actions',
        align: 'right',
        render: (rowData) => {
          const currentLms = normalizeDeliveryLms(rowData.currentDeliveryLms);
          const next = currentLms === 'canvas' ? 'd2l' : 'canvas';
          const facilityLabel = rowData.locationName || rowData.shortCode;
          // For D2L → Canvas the row button is gated by the provisioning
          // checklist that lives in the provisioning dialog; clicking the
          // row button opens the dialog instead. D2L direction stays a
          // direct one-click action.
          const goingToCanvas = next === 'canvas';
          const handleSwitchClick = () => (goingToCanvas ? openProvisioning(rowData) : requestSwitch(rowData));
          const switchTooltip = goingToCanvas
            ? 'Open provisioning to verify the readiness checklist before switching to Canvas.'
            : '';
          return (
            <Box display="inline-flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <Tooltip title={switchTooltip}>
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<SwapHorizRoundedIcon />}
                    onClick={handleSwitchClick}
                    data-testid={`facility-lms-switch-${rowData.locationKey}`}
                    sx={{ minWidth: 152, whiteSpace: 'nowrap' }}
                  >
                    Switch to {LMS_DISPLAY_NAMES[next]}
                  </Button>
                </span>
              </Tooltip>
              {currentLms === 'canvas' && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<CloudSyncRoundedIcon />}
                  onClick={() => openProvisioning(rowData)}
                  aria-label={`Canvas provisioning for ${facilityLabel}`}
                  data-testid={`facility-lms-provision-${rowData.locationKey}`}
                  sx={{ minWidth: 168, whiteSpace: 'nowrap' }}
                >
                  Canvas Provisioning &amp; jobs
                </Button>
              )}
              <Tooltip title="View switch history">
                <IconButton
                  size="small"
                  onClick={() => openHistory(rowData)}
                  aria-label={`History for ${facilityLabel}`}
                  data-testid={`facility-lms-history-${rowData.locationKey}`}
                >
                  <HistoryRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      requestSwitch,
      openHistory,
      openProvisioning,
      classes,
      selectedIds,
      toggleRow,
      toggleSelectAllOnPage,
      currentPageRows,
      pageSelectionState,
    ]
  );

  const gridRows = useMemo(
    () =>
      filteredRows.map((row) => ({
        isSelected: selectedIds.has(String(row.locationKey)),
        rowData: {
          id: row.locationKey,
          locationKey: row.locationKey,
          locationName: row.locationName || '',
          shortCode: row.shortCode || '',
          currentDeliveryLms: row.currentDeliveryLms,
          defaultApplied: row.defaultApplied,
          learnerCount: row.learnerCount,
          canvasSubAccountId: row.canvasSubAccountId ?? null,
          parentProvisioned: row.parentProvisioned ?? null,
        },
      })),
    [filteredRows, selectedIds]
  );

  const historyFacilityName =
    historyModal?.row?.locationName || historyModal?.row?.shortCode || 'Facility';

  const provisioningRow = provisioningModal?.row;
  const provisioningFacilityName =
    provisioningRow?.locationName || provisioningRow?.shortCode || 'Facility';

  const confirmRow = confirm?.row;
  const affectedLearners = confirmRow ? sumLearnerCounts([confirmRow]) : null;

  return (
    <ThemeProvider theme={superAdminTheme}>
      <Box sx={{ p: { xs: 2, md: '24px 32px' }, height: '100%', width: '100%', backgroundColor: 'common.white', overflow: 'auto' }}>
        <Box mb={2}>
          <Typography variant="h1" sx={{ color: 'text.primary' }}>
            Facility LMS Cutover
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Move each facility between D2L and Canvas. Facilities that haven&apos;t been configured stay on D2L.
          </Typography>
        </Box>

        {!error && (
          <Box mb={2}>
            <MigrationStatStrip stats={stats} />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 1.5,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            placeholder="Search by facility name or short code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="facility-lms-search"
            inputProps={{ 'aria-label': 'Search facilities by name or short code' }}
            sx={{
              flexGrow: 1,
              maxWidth: { md: 440 },
              '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                    <ClearRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={lmsFilter}
            onChange={(_event, value) => {
              if (value !== null) setLmsFilter(value);
            }}
            aria-label="Filter facilities by current LMS"
            data-testid="facility-lms-filter"
            sx={{
              ml: { md: 'auto' },
              flexShrink: 0,
              backgroundColor: 'common.white',
              '& .MuiToggleButton-root': {
                height: CONTROL_HEIGHT,
                px: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.secondary',
                borderColor: 'divider',
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.light' },
                },
              },
            }}
          >
            {LMS_FILTER_OPTIONS.map((option) => (
              <ToggleButton
                key={option.value}
                value={option.value}
                data-testid={`facility-lms-filter-${String(option.value).toLowerCase()}`}
              >
                {option.label} ({filterCounts[option.countKey]})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {!error && (
          <>
            <FacilityLmsBulkBar
              selectedCount={selectedCount}
              eligibleOnPageCount={pageSelectionState.eligibleOnPageCount}
              onProvisionSelected={handleBulkProvision}
              onClearSelection={clearSelection}
              loading={bulkSubmitting}
            />
            <Box display="flex" justifyContent="flex-end" mb={bulkJobsPanelOpen ? 0 : 2}>
              <Button
                size="small"
                variant="text"
                startIcon={<HistoryRoundedIcon />}
                onClick={() => setBulkJobsPanelOpen((open) => !open)}
                data-testid="facility-lms-bulk-jobs-toggle"
              >
                {bulkJobsPanelOpen ? 'Hide bulk provisioning jobs' : 'View bulk provisioning jobs'}
              </Button>
            </Box>
            {bulkJobsPanelOpen && (
              <FacilityBulkJobsPanel
                jobs={bulkJobs.jobs}
                loading={bulkJobs.loading}
                error={bulkJobs.error}
                submitting={bulkJobs.submitting}
                onRefresh={bulkJobs.refresh}
                onRetry={bulkJobs.retry}
                onRefreshFacilities={load}
              />
            )}
          </>
        )}

        {error ? (
          <StateBlock
            variant="error"
            title="Failed to load facilities"
            description="Something went wrong while loading the facility list. Check your connection and try again."
            onRetry={load}
            data-testid="facility-lms-error"
          />
        ) : (
          <DataTable
            columns={columns}
            rows={gridRows}
            loading={loading}
            title="Facilities"
            subtitle={subtitle}
            onRefresh={refreshFacilities}
            paginationMode="client"
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            data-testid="facility-lms-grid"
            emptyState={
              <StateBlock
                variant="info"
                title="No facilities match your filters"
                description="Try clearing the search box or switching the LMS filter."
                data-testid="facility-lms-empty"
              />
            }
          />
        )}

        <Dialog
          open={Boolean(historyModal)}
          onClose={closeHistoryModal}
          maxWidth="sm"
          fullWidth
          data-testid="facility-lms-history-dialog"
        >
          <DialogTitle>LMS history — {historyFacilityName}</DialogTitle>
          <DialogContent dividers>
            <HistoryDialogBody historyModal={historyModal} />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeHistoryModal} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(provisioningModal)}
          onClose={closeProvisioningModal}
          maxWidth="md"
          fullWidth
          data-testid="facility-lms-provision-dialog"
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              p: 0,
              position: 'relative',
              background: 'linear-gradient(135deg, #3B1E8F 0%, #5B3FD9 100%)',
              color: '#FFFFFF',
            }}
          >
            {/*
             * Wireframe-aligned colorful header for the Canvas Provisioning
             * dialog. We keep the literal phrase "Canvas provisioning —
             * {facilityName}" available to assistive tech (visually hidden)
             * so screen-reader users still hear the original title and so
             * existing automated checks that probe for that exact label
             * keep working.
             */}
            <Box
              component="span"
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                p: 0,
                m: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              Canvas provisioning — {provisioningFacilityName}
            </Box>
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <CloudSyncRoundedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.85)',
                  }}
                >
                  Canvas Provisioning
                </Typography>
              </Box>
              <Typography
                variant="h5"
                component="div"
                sx={{ fontWeight: 700, lineHeight: 1.2, color: '#FFFFFF', mb: 0.5 }}
              >
                Provision to Canvas
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.82)', mb: provisioningRow?.shortCode ? 1.5 : 0 }}
              >
                {provisioningFacilityName}
              </Typography>
              {provisioningRow?.shortCode && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip
                    size="small"
                    label={provisioningRow.shortCode}
                    sx={{
                      height: 22,
                      color: '#FFFFFF',
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      '& .MuiChip-label': { px: 1, fontWeight: 600, letterSpacing: '0.02em' },
                    }}
                  />
                  {normalizeDeliveryLms(provisioningRow.currentDeliveryLms) === 'canvas' && (
                    <Chip
                      size="small"
                      label="On Canvas"
                      sx={{
                        height: 22,
                        color: '#FFFFFF',
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        '& .MuiChip-label': { px: 1, fontWeight: 600, letterSpacing: '0.02em' },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <IconButton
              aria-label="Close Canvas provisioning"
              onClick={closeProvisioningModal}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: 'rgba(255,255,255,0.9)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {provisioningRow && (
              <ProvisioningJobsPanel
                facility={provisioningRow}
                embedded
                onSwitchToCanvas={requestSwitchFromDialog}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeProvisioningModal} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(confirm)} onClose={() => !switching && setConfirm(null)} maxWidth="sm" fullWidth data-testid="facility-lms-confirm-dialog">
          <DialogTitle>Confirm LMS switch</DialogTitle>
          <DialogContent>
            {confirmRow && (
              <>
                <DialogContentText component="div">
                  Switch <strong>{confirmRow.locationName || confirmRow.shortCode}</strong> from{' '}
                  <strong>{normalizeDeliveryLms(confirmRow.currentDeliveryLms).toUpperCase()}</strong> to{' '}
                  <strong>{confirm.nextLms.toUpperCase()}</strong>?
                </DialogContentText>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'warning.light',
                    color: 'warning.main',
                  }}
                >
                  <WarningAmberRoundedIcon fontSize="small" />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {affectedLearners != null ? (
                      <>
                        Delivery changes immediately. This affects <strong>{affectedLearners.toLocaleString()}</strong>{' '}
                        active learner{affectedLearners === 1 ? '' : 's'} and any in-progress courses at this facility.
                      </>
                    ) : (
                      <>
                        Delivery changes immediately. This affects active learners and any in-progress courses at this
                        facility.
                      </>
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setConfirm(null)} disabled={switching} color="inherit">
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={<SwapHorizRoundedIcon />}
              onClick={onConfirmSwitch}
              disabled={switching}
              data-testid="facility-lms-confirm-switch"
            >
              {switching ? 'Switching…' : 'Confirm switch'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(toast)}
          autoHideDuration={6000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {toast ? (
            <Alert severity={toast.type} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
              {toast.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export { FacilityLmsCutover };
export default withStyles(styles)(FacilityLmsCutover);
